export const strategyOverview = [
  {
    id: 'scalping',
    name: 'Scalping',
    tagline: 'Seconds to minutes — speed is the game',
    icon: 'ri-flashlight-line',
    holdTime: 'Seconds to minutes',
    tradesPerDay: '15 – 50',
    targetPipsPerTrade: '3 – 10 pips',
    colorRole: 'primary',
    dominantCost: 'Commission & Spread',
    dominantCostLabel: 'Entry costs dominate everything',
    desc: 'Scalpers open and close positions in minutes. They never hold overnight — so swap does not apply. Their entire cost is entry costs: spread plus commission. A 0.1 pip difference in spread can mean thousands of dollars per year.',
    costPyramid: {
      top: 'Commission',
      topPct: 65,
      middle: 'Spread',
      middlePct: 25,
      bottom: 'Slippage',
      bottomPct: 10,
    },
  },
  {
    id: 'day-trading',
    name: 'Day Trading',
    tagline: 'Intraday moves — all positions closed by 5 PM',
    icon: 'ri-sun-line',
    holdTime: 'Minutes to hours',
    tradesPerDay: '2 – 8',
    targetPipsPerTrade: '15 – 50 pips',
    colorRole: 'accent',
    dominantCost: 'Spread & Commission',
    dominantCostLabel: 'Entry costs still primary, swap avoidable',
    desc: 'Day traders close all positions before the daily rollover. Swap is avoided entirely by design. The primary costs are spread and commission — similar to scalping but at lower frequency and larger position sizes.',
    costPyramid: {
      top: 'Spread',
      topPct: 50,
      middle: 'Commission',
      middlePct: 35,
      bottom: 'Slippage',
      bottomPct: 15,
    },
  },
  {
    id: 'swing-trading',
    name: 'Swing Trading',
    tagline: 'Days to weeks — holding costs enter the picture',
    icon: 'ri-moon-clear-line',
    holdTime: '2 – 20 days',
    tradesPerDay: '0.5 – 2',
    targetPipsPerTrade: '50 – 200 pips',
    colorRole: 'secondary',
    dominantCost: 'Swap / Rollover',
    dominantCostLabel: 'Holding costs overtake entry costs',
    desc: 'Swing traders hold positions for days or weeks. Swap charges accumulate silently — a position held 10 days at 0.8 pips/day costs 8 pips in swap alone, often exceeding the original spread. Swap is the stealth cost that turns winning trades into losers.',
    costPyramid: {
      top: 'Swap',
      topPct: 50,
      middle: 'Spread',
      middlePct: 30,
      bottom: 'Commission',
      bottomPct: 20,
    },
  },
  {
    id: 'position-trading',
    name: 'Position Trading',
    tagline: 'Weeks to months — the long game',
    icon: 'ri-calendar-check-line',
    holdTime: 'Weeks to months',
    tradesPerDay: '0.1 – 0.5',
    targetPipsPerTrade: '200 – 1000+ pips',
    colorRole: 'foreground',
    dominantCost: 'Swap / Rollover',
    dominantCostLabel: 'Swap is the silent budget-killer',
    desc: 'Position traders hold for weeks or months. With 30+ days of holding, swap can accumulate to 24+ pips — far exceeding the entry spread. A position that looks profitable on entry can slowly bleed to break-even through overnight charges.',
    costPyramid: {
      top: 'Swap',
      topPct: 70,
      middle: 'Spread',
      middlePct: 20,
      bottom: 'Commission',
      bottomPct: 10,
    },
  },
] as const;

export const costBreakdownByStrategy = [
  {
    strategy: 'Scalping',
    trader: 'EURUSD Scalper',
    assumptions: '20 RT/day, 0.1 lots, 20 trading days, ECN account ($3.50/side, 0.2 pip spread)',
    costs: [
      { name: 'Commission', monthly: 280.00, annual: 3360, pct: 68, detail: '400 trades × 0.1 lots × $7 RT' },
      { name: 'Spread', monthly: 80.00, annual: 960, pct: 19, detail: '400 trades × 0.1 lots × 0.2 pips × $10' },
      { name: 'Slippage (est.)', monthly: 40.00, annual: 480, pct: 10, detail: '5% of trades at 1 pip slippage' },
      { name: 'Swap', monthly: 0, annual: 0, pct: 0, detail: 'No overnight holds — swap-free by design' },
    ],
    totalMonthly: 400.00,
    totalAnnual: 4800,
    keyInsight: 'Commission is 3.5× larger than spread. Every $1 reduction in commission saves $280/month. This is why scalpers negotiate commission rates aggressively.',
    targetPips: 5,
    costAsPctOfTarget: 'Commission eats ~1.4 pips of a 5-pip target (28% of the profit target).',
  },
  {
    strategy: 'Day Trading',
    trader: 'GBPUSD Day Trader',
    assumptions: '5 RT/day, 0.5 lots, 20 trading days, ECN account ($3.50/side, 0.4 pip spread)',
    costs: [
      { name: 'Spread', monthly: 200.00, annual: 2400, pct: 48, detail: '100 trades × 0.5 lots × 0.4 pips × $10' },
      { name: 'Commission', monthly: 175.00, annual: 2100, pct: 42, detail: '100 trades × 0.5 lots × $3.50 × 2 sides' },
      { name: 'Slippage (est.)', monthly: 40.00, annual: 480, pct: 10, detail: '5% of trades at 0.8 pip slippage' },
      { name: 'Swap', monthly: 0, annual: 0, pct: 0, detail: 'All positions closed before 5 PM ET rollover' },
    ],
    totalMonthly: 415.00,
    totalAnnual: 4980,
    keyInsight: 'Spread and commission are nearly equal for day traders. Optimizing either one helps — the combined entry cost is what matters.',
    targetPips: 25,
    costAsPctOfTarget: 'Entry costs consume ~1.6 pips of a 25-pip target (6.4%). Much less impactful than for scalpers.',
  },
  {
    strategy: 'Swing Trading',
    trader: 'EURUSD Swing Trader',
    assumptions: '1 trade/day, 2.0 lots, 20 trading days, avg hold 7 days, standard account (1.2 pip spread)',
    costs: [
      { name: 'Swap', monthly: 560.00, annual: 6720, pct: 52, detail: '5 positions × 2 lots × 7 days × 0.8 pips × $10' },
      { name: 'Spread', monthly: 480.00, annual: 5760, pct: 44, detail: '20 trades × 2.0 lots × 1.2 pips × $10' },
      { name: 'Commission', monthly: 0, annual: 0, pct: 0, detail: 'Standard account — commission built into spread' },
      { name: 'Slippage (est.)', monthly: 40.00, annual: 480, pct: 4, detail: 'Limit orders used — minimal slippage' },
    ],
    totalMonthly: 1080.00,
    totalAnnual: 12960,
    keyInsight: 'Swap is the dominant cost at 52% of total. Holding one extra day on 5 positions adds $80/month. Plan exits carefully — every day costs money.',
    targetPips: 80,
    costAsPctOfTarget: 'Total costs consume ~5.4 pips of an 80-pip target (6.8%). Swap alone is ~2.8 pips.',
  },
  {
    strategy: 'Position Trading',
    trader: 'USDJPY Position Trader',
    assumptions: '6 trades/month, 5.0 lots, avg hold 30 days, ECN account ($3.50/side, 0.2 pip spread)',
    costs: [
      { name: 'Swap', monthly: 675.00, annual: 8100, pct: 70, detail: '3 positions × 5 lots × 30 days × 0.5 pips × $9' },
      { name: 'Spread', monthly: 135.00, annual: 1620, pct: 14, detail: '6 trades × 5.0 lots × 0.2 pips × $9' },
      { name: 'Commission', monthly: 105.00, annual: 1260, pct: 11, detail: '6 trades × 5.0 lots × $3.50 × 2 sides' },
      { name: 'Slippage (est.)', monthly: 45.00, annual: 540, pct: 5, detail: 'Limit orders — minimal but not zero' },
    ],
    totalMonthly: 960.00,
    totalAnnual: 11520,
    keyInsight: 'Swap is 70% of total costs — the entry spread and commission are footnotes. The holding period drives the economics. Shaving 5 days off the average hold saves $112/month.',
    targetPips: 300,
    costAsPctOfTarget: 'Total costs consume ~10.7 pips of a 300-pip target (3.6%). Swap is ~7.5 pips over 30 days.',
  },
] as const;

export const whichCostDominates = [
  {
    costType: 'Spread',
    icon: 'ri-arrow-left-right-line',
    colorRole: 'primary',
    strategies: [
      { name: 'Scalping', impact: 'High', note: '0.1 pip matters — $800/year on 20 RT/day' },
      { name: 'Day Trading', impact: 'High', note: 'Larger lots amplify spread cost' },
      { name: 'Swing Trading', impact: 'Medium', note: 'One entry per trade — less cumulative' },
      { name: 'Position Trading', impact: 'Low', note: 'Spread is a rounding error vs 300-pip moves' },
    ],
  },
  {
    costType: 'Commission',
    icon: 'ri-bank-card-line',
    colorRole: 'accent',
    strategies: [
      { name: 'Scalping', impact: 'Critical', note: 'Dominant cost — $3.50 difference = $280/month' },
      { name: 'Day Trading', impact: 'High', note: 'Roughly equal to spread cost' },
      { name: 'Swing Trading', impact: 'Low', note: 'Low trade frequency minimizes impact' },
      { name: 'Position Trading', impact: 'Negligible', note: '6 trades/month — commission is a footnote' },
    ],
  },
  {
    costType: 'Swap / Rollover',
    icon: 'ri-moon-clear-line',
    colorRole: 'secondary',
    strategies: [
      { name: 'Scalping', impact: 'None', note: 'No overnight positions held' },
      { name: 'Day Trading', impact: 'None', note: 'Closed before rollover by design' },
      { name: 'Swing Trading', impact: 'Critical', note: 'Dominant cost — can exceed total entry costs' },
      { name: 'Position Trading', impact: 'Critical', note: '70% of all costs — the silent budget killer' },
    ],
  },
  {
    costType: 'Slippage',
    icon: 'ri-speed-up-line',
    colorRole: 'foreground',
    strategies: [
      { name: 'Scalping', impact: 'Medium', note: 'Market orders during news = risky' },
      { name: 'Day Trading', impact: 'Medium', note: 'News events increase slippage risk' },
      { name: 'Swing Trading', impact: 'Low', note: 'Limit orders reduce slippage to near zero' },
      { name: 'Position Trading', impact: 'Low', note: 'A few pips of slippage on a 300-pip target' },
    ],
  },
] as const;

export const strategyCostFAQs = [
  {
    question: 'Which trading strategy has the lowest total costs?',
    answer: 'Position trading has the lowest cost as a percentage of profit target (3.6%), but the highest absolute dollar cost ($960/month). Day trading has the most balanced cost profile — moderate absolute costs ($415/month) and moderate cost-to-target ratio (6.4%). Scalping has the lowest absolute cost ($400/month) but the highest cost-to-target ratio (28%) — meaning costs eat the largest slice of each trade\'s potential profit. There is no universally "cheapest" strategy — it depends on whether you optimize for absolute dollars or percentage of profit.',
    category: 'Strategy Comparison',
  },
  {
    question: 'Why do scalpers care more about commission than spread?',
    answer: 'Because of trade frequency. A scalper doing 400 trades/month at 0.1 lots pays $280 in commission (at $7/RT) vs $80 in spread (at 0.2 pips). Commission is 3.5× larger. Every $1 reduction in round-turn commission saves $280/month — $3,360/year. For a scalper targeting 5 pips per trade, commission alone eats 1.4 pips (28% of the target). This is why professional scalpers negotiate commission rates down to $2–$3/RT if they trade enough volume.',
    category: 'Scalping',
  },
  {
    question: 'How can day traders minimize their trading costs?',
    answer: 'Three levers: (1) Trade during liquid sessions — London/NY overlap gives the tightest spreads, saving 0.3–0.5 pips per trade. (2) Use limit orders instead of market orders — eliminates slippage and gives you the exact price you want. (3) Choose the right account type — at 100 trades/month, the ECN account saves ~$300/month over spread-only. Day traders are in a sweet spot where both spread and commission matter roughly equally — optimize both.',
    category: 'Day Trading',
  },
  {
    question: 'At what holding period does swap become the biggest cost?',
    answer: 'For EURUSD at typical rates (1.2 pip spread, 0.6 pip/day swap), swap exceeds spread cost after holding for 2 days. At 7 days, swap is 3.5× the spread cost. At 30 days, swap is 15× the spread. The crossover happens surprisingly fast — if you hold any position for more than 48 hours, swap should be your primary cost concern. This is why day traders (who never hold overnight) can ignore swap entirely while swing traders must track it obsessively.',
    category: 'Swing Trading',
  },
  {
    question: 'Do position traders even need to think about spread?',
    answer: 'Not really — at least not as a primary concern. A position trader targeting 300 pips on USDJPY pays 0.2 pips in spread ($9 at 5 lots). That is 0.07% of the target. Compare to swap: 30 days at 0.5 pips/day = 15 pips ($675). Spread cost is literally a rounding error. Position traders should optimize for: (1) swap rates on their primary pairs, (2) whether the broker offers swap-free Islamic accounts, and (3) rollover policies (some brokers charge triple swap on Wednesdays).',
    category: 'Position Trading',
  },
  {
    question: 'Can I combine strategies to optimize costs?',
    answer: 'Yes — and many professional traders do. Example: scalp during London/NY overlap (tightest spreads, lowest commission impact), swing trade pairs with favorable swap rates, and avoid holding positions through Wednesday rollover (triple swap day). You can also use different accounts: an ECN account for scalping and a standard account for swing trading to avoid commission on low-frequency trades. The key is matching your account type and trading hours to the specific strategy you are running at that moment.',
    category: 'Strategy Comparison',
  },
  {
    question: 'How do news events change the cost equation for each strategy?',
    answer: 'Drastically — but differently for each style. Scalpers: spreads can widen 5–10× during NFP, turning a profitable scalp into a guaranteed loss. Most scalpers sit out news entirely. Day traders: slippage risk spikes; a market order during FOMC can slip 3–5 pips. Swing traders: news creates volatility that can hit stops, but spread widening is temporary and averages out over the holding period. Position traders: news is background noise — the spread spike for 15 minutes is irrelevant over a 30-day hold.',
    category: 'Strategy Comparison',
  },
  {
    question: 'What is the single biggest cost mistake traders make by strategy?',
    answer: 'Scalpers: trading during off-peak hours — the spread premium eats profits. Day traders: using market orders around news — a single 3-pip slippage can wipe out the profit from 3 clean trades. Swing traders: ignoring swap rates — a "profitable" trade held 20 days can go negative purely from overnight charges. Position traders: paying ECN commission — the $7/RT on 6 trades/month is $42 total; not worth optimizing vs the thousands in swap costs that actually matter.',
    category: 'Strategy Comparison',
  },
  {
    question: 'Should I use different brokers for different strategies?',
    answer: 'It can make sense. Example: Broker A for scalping (tightest ECN spreads, lowest commission, fastest execution), Broker B for swing trading (best swap rates, swap-free account option, wide instrument range). The optimal scalping broker is rarely the optimal swing trading broker — they optimize for different things. Just be aware of the administrative overhead: multiple accounts to fund, multiple platforms to learn, and tax reporting complexity. For most retail traders, one good all-around broker is sufficient.',
    category: 'Strategy Comparison',
  },
  {
    question: 'How do I calculate my break-even after costs for my specific strategy?',
    answer: 'Formula: Break-even pips = (Spread in pips) + (Commission per lot / Pip Value) + (Swap × Days Held). For a day trader on EURUSD ECN: 0.2 pips spread + ($7 RT / $10 per pip) + (0 × 0) = 0.2 + 0.7 = 0.9 pips to break even. For a swing trader holding 7 days: 1.2 pips spread + $0 commission + (0.8 pips × 7 days) = 1.2 + 5.6 = 6.8 pips to break even. If your average win is below this number after costs, your strategy has a negative expectancy.',
    category: 'Strategy Comparison',
  },
  {
    question: 'Does the account currency affect strategy costs?',
    answer: 'Yes — indirectly through conversion fees. If your account is in EUR but you trade USDJPY, every trade involves a EUR→USD→JPY conversion chain. Most brokers charge 0.3–0.5% on these conversions. For a scalper doing $1M in notional volume per month, that is $3,000–$5,000/month in hidden conversion costs — far exceeding spread and commission combined. Always match your account currency to your primary trading pair\'s base currency whenever possible.',
    category: 'Strategy Comparison',
  },
  {
    question: 'How should a beginner choose which strategy to start with based on costs?',
    answer: 'Start with day trading — it has the most balanced cost profile and avoids the two biggest cost traps: commission-dominant scalping (where tiny rate differences matter enormously) and swap-dominant swing trading (where holding costs silently accumulate). Day trading has moderate absolute costs ($400–500/month for active traders), allows you to learn execution quality, and keeps the cost structure simple (spread + commission only, no swap). Master day trading costs first, then branch into scalping or swing trading once you understand your personal cost per trade.',
    category: 'Strategy Comparison',
  },
] as const;

export const relatedStrategyGuides = [
  {
    title: 'Forex Spread Explained',
    href: '/forex-spread/',
    desc: 'The bid-ask gap is the first cost every trader pays, no exceptions. See how wide it gets on different pairs and sessions.',
    icon: 'ri-arrow-left-right-line',
  },
  {
    title: 'Forex Commission Explained',
    href: '/forex-commission/',
    desc: 'If you\'re scalping, commission might be eating more profit than spread. Here\'s the math every high-frequency trader should know.',
    icon: 'ri-bank-card-line',
  },
  {
    title: 'Spread vs Commission',
    href: '/spread-vs-commission/',
    desc: 'Which actually costs less — spread-only or ECN with commission? The answer depends entirely on your volume and pair choice.',
    icon: 'ri-scales-3-line',
  },
  {
    title: 'Forex Swap Explained',
    href: '/forex-swap/',
    desc: 'Overnight interest can flip from small to massive when you hold for weeks. Position traders, this one is for you.',
    icon: 'ri-moon-clear-line',
  },
] as const;

export const costOptimizationTactics = [
  {
    strategyId: 'scalping',
    strategyName: 'Scalping',
    dominantCost: 'Commission',
    colorRole: 'primary',
    subtitle: 'Every dollar in commission is 400× leveraged by trade frequency.',
    tactics: [
      {
        title: 'Negotiate your commission rate',
        desc: 'At 400 trades/month, reducing commission from $7 to $5 per round-turn saves $800/month. Most brokers offer volume-based tier discounts — ask for them. If you trade 0.5+ lots per trade, you have leverage.',
        impact: 'Save $800/mo',
        icon: 'ri-discount-percent-line',
      },
      {
        title: 'Trade only during peak liquidity',
        desc: 'London/NY overlap (8 AM–12 PM ET) offers spreads 0.1–0.3 pips tighter than Asian session. For 400 monthly trades at 0.1 lots, that is $40–$120 saved on spread alone — plus faster fills reduce slippage.',
        impact: 'Save $40–120/mo',
        icon: 'ri-timer-flash-line',
      },
      {
        title: 'Use limit orders, not market orders',
        desc: 'A market order during a volatile second can slip 1–2 pips. At 400 trades, even 10% slippage rate at 1 pip costs $40/month. Limit orders eliminate this entirely and give you the exact entry you calculated.',
        impact: 'Eliminate slippage',
        icon: 'ri-focus-3-line',
      },
    ],
  },
  {
    strategyId: 'day-trading',
    strategyName: 'Day Trading',
    dominantCost: 'Spread + Commission',
    colorRole: 'accent',
    subtitle: 'Spread and commission are neck-and-neck. Optimize both — neither alone is enough.',
    tactics: [
      {
        title: 'Choose ECN over Standard if volume is high',
        desc: 'At 100 trades/month with 0.5 lots, an ECN account ($7/RT + 0.2 pip spread) costs $415 total. A Standard account (1.2 pip spread, no commission) costs $600. The crossover is around 30 trades/month — above that, ECN wins.',
        impact: 'Save ~$185/mo',
        icon: 'ri-scales-3-line',
      },
      {
        title: 'Avoid news trading unless you are fast',
        desc: 'During NFP or FOMC, spreads can widen 5–10× and slippage can hit 3–5 pips per fill. A single bad news trade can cost more than a week of normal trading costs. Sit out the first 15 minutes, then re-enter.',
        impact: 'Avoid 5–10× cost spike',
        icon: 'ri-alert-line',
      },
      {
        title: 'Trade fewer pairs, go deeper',
        desc: 'Spreading 8 trades across 6 pairs means you never learn any pair\'s spread rhythm. Focus on 2–3 pairs and learn exactly when their spreads tighten (EURUSD at London open, USDJPY at Tokyo/London overlap). Consistency beats diversification for costs.',
        impact: '~0.2 pip/trade saved',
        icon: 'ri-stack-line',
      },
    ],
  },
  {
    strategyId: 'swing-trading',
    strategyName: 'Swing Trading',
    dominantCost: 'Swap / Rollover',
    colorRole: 'secondary',
    subtitle: 'The holding cost is the one you never see on the trade ticket. Plan your exits around it.',
    tactics: [
      {
        title: 'Trade pairs with favorable swap rates',
        desc: 'Long AUDJPY earns positive swap (you get paid to hold). Long EURTRY costs negative swap (you pay to hold). Before entering any swing trade, check the swap rate — a favorable swap can add 5–15 pips to your profit over a 7-day hold instead of subtracting it.',
        impact: 'Flip swap from cost → income',
        icon: 'ri-exchange-funds-line',
      },
      {
        title: 'Avoid holding through Wednesday rollover',
        desc: 'Most brokers charge triple swap on Wednesday (to account for the weekend). A position held Mon–Fri pays 5 days of swap. A position held Wed–Thu alone pays 3 days. Close before Wednesday 5 PM ET or open after to dodge the triple charge.',
        impact: 'Save 3 days of swap',
        icon: 'ri-calendar-check-line',
      },
      {
        title: 'Set profit targets that respect the swap clock',
        desc: 'If your target is 50 pips but swap costs 0.8 pips/day, a 7-day hold costs 5.6 pips (~11% of target). A 14-day hold costs 11.2 pips (~22%). Every extra day shrinks your net profit. Set exits that balance profit potential against holding costs.',
        impact: 'Protect 10–20% of profit',
        icon: 'ri-crosshair-line',
      },
    ],
  },
  {
    strategyId: 'position-trading',
    strategyName: 'Position Trading',
    dominantCost: 'Swap / Rollover',
    colorRole: 'foreground',
    subtitle: 'After 30 days, entry costs are a rounding error. Swap is the entire game.',
    tactics: [
      {
        title: 'Use a swap-free Islamic account if eligible',
        desc: 'Swap-free accounts replace overnight interest with a fixed admin fee (typically $5–$10 per lot per side). For a 5-lot position held 30 days, swap costs $675. A $10 admin fee is 98.5% cheaper. This single change transforms the economics of position trading.',
        impact: 'Save $600+/mo',
        icon: 'ri-hand-heart-line',
      },
      {
        title: 'Shorten the average hold by 5 days',
        desc: 'A USDJPY position held 30 days at 0.5 pips/day costs 15 pips in swap. Held 25 days, it costs 12.5 pips — saving 2.5 pips per position. At 5 lots, that is $112.50 saved per trade. Be slightly more aggressive with profit targets; the swap savings compound.',
        impact: 'Save $112/trade',
        icon: 'ri-speed-up-line',
      },
      {
        title: 'Ignore spread and commission entirely',
        desc: 'At 6 trades/month with 5 lots, ECN commission is $105 total. Optimizing this is a distraction — the real battle is swap at $675/month. Do not waste energy negotiating commission when swap is 6× larger. Focus 100% of your cost optimization on swap rates.',
        impact: 'Focus where it matters',
        icon: 'ri-eye-line',
      },
    ],
  },
] as const;

export const accountTypeGuide = [
  {
    accountType: 'ECN / Raw Spread',
    bestFor: ['Scalping', 'Day Trading'],
    worstFor: ['Position Trading'],
    spread: '0.0 – 0.3 pips',
    commission: '$3.50 – $7.00 / RT',
    swapMarkup: 'Standard',
    monthlyEstimate: {
      scalping: '$400',
      dayTrading: '$415',
      swingTrading: '$1,080',
      positionTrading: '$960',
    },
    keyVerdict: 'Wins when trade frequency is high. The commission is fixed per trade, but the spread is razor-thin — perfect for scalpers and day traders who execute hundreds of trades.',
  },
  {
    accountType: 'Standard / Spread-Only',
    bestFor: ['Swing Trading'],
    worstFor: ['Scalping'],
    spread: '1.0 – 1.5 pips',
    commission: '$0 (built into spread)',
    swapMarkup: 'Standard',
    monthlyEstimate: {
      scalping: '$1,200',
      dayTrading: '$600',
      swingTrading: '$1,080',
      positionTrading: '$1,380',
    },
    keyVerdict: 'Simpler pricing but the wide spread murders scalpers. For swing traders, it costs roughly the same as ECN at this frequency — the built-in spread markup cancels out any commission savings. If you swing trade, the only way to meaningfully cut costs is a Swap-Free account.',
  },
  {
    accountType: 'Swap-Free / Islamic',
    bestFor: ['Position Trading', 'Swing Trading'],
    worstFor: ['Scalping'],
    spread: 'Varies by broker',
    commission: 'Varies by broker',
    swapMarkup: 'None (flat admin fee)',
    monthlyEstimate: {
      scalping: 'N/A',
      dayTrading: 'N/A',
      swingTrading: '~$480',
      positionTrading: '~$285',
    },
    keyVerdict: 'Eliminates the largest cost for position and swing traders. A flat $5–$10 admin fee replaces hundreds in swap charges. Check eligibility requirements — not all brokers offer this, and terms vary.',
  },
  {
    accountType: 'Pro / VIP Tier',
    bestFor: ['Scalping', 'Day Trading'],
    worstFor: ['Swing Trading', 'Position Trading'],
    spread: '0.0 – 0.2 pips',
    commission: '$2.00 – $4.00 / RT',
    swapMarkup: 'Reduced',
    monthlyEstimate: {
      scalping: '$240',
      dayTrading: '$275',
      swingTrading: '$720',
      positionTrading: '$780',
    },
    keyVerdict: 'Requires high deposit ($25K+) but slashes commission by 40–50%. For high-frequency scalpers, tighter spreads and halved commissions save $160/month vs ECN — $1,920/year in your pocket.',
  },
] as const;

export const perTradeCostReference = [
  {
    strategy: 'Scalping',
    trader: 'EURUSD Scalper',
    lotSize: '1.0 lots',
    costs: [
      { name: 'Commission', amount: '$7.00', note: '$3.50/side × 1 lot × 2' },
      { name: 'Spread', amount: '$2.00', note: '0.2 pips × $10/pip (1 lot)' },
      { name: 'Slippage (avg)', amount: '$1.00', note: 'Estimated 10% of trades' },
      { name: 'Swap', amount: '$0', note: 'No overnight hold' },
    ],
    totalPerTrade: '$10.00',
    breakEvenPips: '1.0 pips',
    targetPips: '5 pips',
    costEatsPct: '20% of target',
  },
  {
    strategy: 'Day Trading',
    trader: 'GBPUSD Day Trader',
    lotSize: '5.0 lots',
    costs: [
      { name: 'Spread', amount: '$20.00', note: '0.4 pips × $50/pip (5 lots)' },
      { name: 'Commission', amount: '$35.00', note: '$3.50/side × 5 lots × 2' },
      { name: 'Slippage (avg)', amount: '$4.00', note: 'Estimated 5% of trades' },
      { name: 'Swap', amount: '$0', note: 'Closed before rollover' },
    ],
    totalPerTrade: '$59.00',
    breakEvenPips: '1.18 pips',
    targetPips: '25 pips',
    costEatsPct: '4.7% of target',
  },
  {
    strategy: 'Swing Trading',
    trader: 'EURUSD Swing Trader',
    lotSize: '2.0 lots',
    costs: [
      { name: 'Swap (1 day)', amount: '$16.00', note: '2 lots × 0.8 pips × $10' },
      { name: 'Spread', amount: '$24.00', note: '1.2 pips × $20/pip' },
      { name: 'Swap (7 days)', amount: '$112.00', note: 'Accumulates to dominate' },
      { name: 'Slippage (avg)', amount: '$2.00', note: 'Limit orders — minimal' },
    ],
    totalPerTrade: '$42.00',
    breakEvenPips: '2.1 pips',
    targetPips: '80 pips',
    costEatsPct: '2.6% of target',
    swapNote: 'Swap costs shown for 1 day. At 7-day hold, total cost = $138.00 (6.9 pips).',
  },
  {
    strategy: 'Position Trading',
    trader: 'USDJPY Position Trader',
    lotSize: '5.0 lots',
    costs: [
      { name: 'Swap (30 days)', amount: '$675.00', note: '5 lots × 0.5 pips × 30d × $9' },
      { name: 'Spread', amount: '$9.00', note: '0.2 pips × $45/pip' },
      { name: 'Commission', amount: '$35.00', note: '$3.50/side × 5 lots × 2' },
      { name: 'Slippage (avg)', amount: '$7.50', note: 'Limit orders — minimal' },
    ],
    totalPerTrade: '$726.50',
    breakEvenPips: '16.1 pips',
    targetPips: '300 pips',
    costEatsPct: '5.4% of target',
    swapNote: 'Total includes full 30-day swap. Entry-only cost (spread + commission + slippage) is just $51.50.',
  },
] as const;